import {Button, Spin} from 'antd'
import {docs_getSubCodiDocumentRows_Api, docs_reArrangeCodiDocumentRow_Api,} from 'apis'
import classNames from 'classnames'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useEffect, useState} from 'react'
import {BsArrowsMove} from 'react-icons/bs'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import DraggableWidget from 'views/Shared/components/DraggableWidget'
import {useRowDetails} from '../CodiDocumentContext'
import {LoadingPlaceholder} from '../functions/renderLoading'

const ArrangeCodiContent = ({
  id,
  params,
  Wrapper = 'div',
  onCancel = NullAsDefault,
  onSuccess = NullAsDefault,
  renderActions = NullAsDefault,
}) => {
  const t = useTranslate()

  const { index_typecd } = params || {}

  const row_id = params?.id

  const [isSubmitting, setSubmitting] = useState(false)

  const [orderedList, setOrderList] = useState([])

  const { item, isLoading = false } = useRowDetails(row_id)

  const { handleAsyncAction = NullAsDefault } = useAsyncAction({
    apiInfo: docs_reArrangeCodiDocumentRow_Api,
    query: bindQueryParams([{ id }, { row_id }]),
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      setSubmitting(false)
      onSuccess()
    },
    onError: (e, o) => {
      setSubmitting(false)
      notifyOnError(t)(e, o)
    },
  })

  const handleSubmit = useCallback(() => {
    if (!_.isEmpty(orderedList)) {
      setSubmitting(true)
      handleAsyncAction({ codi_document_row_ids: orderedList.join(',') })
    }
  }, [orderedList])

  const onDrop = ({ items, draggableId, destination: order_number }) => {
    setOrderList(items)
  }

  const { response, isLoading: fetchingSubRows = false } = useAsync({
    query: bindQueryParams([{ id }, { row_id }]),
    apiInfo: docs_getSubCodiDocumentRows_Api,
  })

  const data = Array.from(getResponseItems(response))

  useEffect(() => {
    const array = data.map(({ id }) => id)
    if (!_.isEqual(array, orderedList)) {
      setOrderList(array)
    }
  }, [response])


  return (
    <Wrapper className="flex flex-col flex-1">
      <div className="flex flex-col mx-auto max-w-2xl w-full gap-2 py-2 px-8 custom-form-codi">
        <div className="flex justify-center font-bold text-color-000 text-xl uppercase">
          {t('arrange content')}
        </div>
        <div className="flex flex-col gap-3">
          <LoadingPlaceholder isLoading={isLoading}>
            <div className="flex flex-col gap-1">
              <span className="uppercase font-semibold text-color-200 border-b tracking-wide">
                {t('content')}
              </span>
              <span className="text-xs italic font-light text-color-400">
                {`( ${t('drag content description')} )`}
              </span>
            </div>
            <span className="text-sm">
              <span className="font-bold text-color-000">
                {_.get(item, 'index_typecd') === 'clause' ||
                _.get(item, 'index_typecd') === 'point'
                  ? `${t(_.get(item, 'index_typecd'))} ${_.get(
                      item,
                      'index_cd'
                    )}: `
                  : `${_.get(item, 'index_cd')}: `}
              </span>
              <span className="font-medium text-color-100">
                {_.get(item, 'content')}
              </span>
            </span>
          </LoadingPlaceholder>
          <Spin spinning={!!isLoading}>
            <Spin spinning={!!fetchingSubRows}>
              <DraggableWidget
                key={orderedList}
                data={Array.from(orderedList || []).map((id) =>
                  _.find(data, { id })
                )}
                sortBy="order_number"
                onCompleted={onDrop}
                withClassNames={(isDragging, classNames) => [
                  ...classNames,
                  'rounded-lg mb-2',
                  isDragging
                    ? 'border-2 border-blue-400'
                    : 'border border-color-50 background',
                ]}
                itemClassName="w-full p-2 flex flex-col space-y-2"
                renderItem={(row = {}, { isDragging = false }) => (
                  <div className="flex gap-3">
                    <BsArrowsMove className="text-color-400 mt-1" />
                    <span
                      className={
                        row.index_typecd === 'content'
                          ? 'flex flex-col flex-1'
                          : 'space-x-1 flex-1 leading-tight'
                      }>
                      <span
                        className={classNames(
                          'text-sm',
                          row.index_typecd === 'content'
                            ? 'font-medium text-color-100 italic'
                            : 'font-semibold text-color-000'
                        )}>
                        {row.index_typecd === 'clause' ||
                        row.index_typecd === 'point'
                          ? `${t(row.index_typecd)} ${row.index_cd} : `
                          : row.index_typecd === 'content'
                          ? `${t(row.index_typecd)} : `
                          : `${row.index_cd} : `}
                      </span>
                      <span className="text-sm text-color-400 italic">
                        {row.title || row.content || null}
                      </span>
                    </span>
                  </div>
                )}
              />
            </Spin>
          </Spin>
        </div>
      </div>
      <div className="flex-1" />
      {renderActions(
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={!!isSubmitting}
          disabled={!!isSubmitting || _.isEmpty(orderedList)}
          className="rounded-lg no-shadow no-text-shadow font-medium">
          {t('save')}
        </Button>,
        { isSubmitting }
      )}
    </Wrapper>
  )
}

export default ArrangeCodiContent
